import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import {
    DataGrid,
    Column,
    Editing,
    Lookup,
    RequiredRule,
    Scrolling,
    Selection,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
    LoadPanel as GridLoadPanel,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import RenderSelectBoxCell from "../../../../../common/RenderCell";
import { CodeLookupLayout } from "../../../../../common/lookups/Layouts/ImLayouts";
import { onContentReady, onEditingStart } from "../../../../../common/Utils";

const SERIAL_NUMBER_RECEIPT_LAYOUT_ID = "7D4F2B91-3C58-4E6A-9B0D-5A1E8C7F2D43";

const yesNoItems = [
    { ID: "Y", Text: "Yes" },
    { ID: "N", Text: "No" },
];

/* Legacy CalcEdit leave events round converted weights to 0 decimals away from zero. */
const roundAwayFromZero = (value) => (value == null ? value : Math.sign(value) * Math.round(Math.abs(value)));

const initialFormFields = {
    AutoGenerateQuantity: null,
    ItemDescription: null,
    LotDescription: null,
    NetWeight: null,
    NetWeight2: null,
    GrossWeight: null,
    GrossWeight2: null,
    Area1: null,
    Area2: null,
    Volume1: null,
    Volume2: null,
    Size1: null,
    Size2: null,
    Reference1: null,
    Reference2: null,
    Code1: null,
    Code2: null,
    ReceiptVerified: "N",
};

/* Legacy netWeight/grossWeight/area/volume Leave events: an edit to one side of a pair
   recomputes the other side through the UOM conversion factor. [factorKey, multiply, rounded] */
const conversionPairs = {
    NetWeight: { target: "NetWeight2", factor: "weight", multiply: true, rounded: true },
    NetWeight2: { target: "NetWeight", factor: "weight", multiply: false, rounded: true },
    GrossWeight: { target: "GrossWeight2", factor: "weight", multiply: true, rounded: true },
    GrossWeight2: { target: "GrossWeight", factor: "weight", multiply: false, rounded: true },
    Area1: { target: "Area2", factor: "area", multiply: true, rounded: false },
    Area2: { target: "Area1", factor: "area", multiply: false, rounded: false },
    Volume1: { target: "Volume2", factor: "volume", multiply: true, rounded: false },
    Volume2: { target: "Volume1", factor: "volume", multiply: false, rounded: false },
};

const numberFormat = { type: "fixedPoint", precision: 2 };
/* Legacy CalcEdit columns show 2 decimals; the editor entry is masked to 2 decimals too. */
const numberEditorOptions = { format: numberFormat };

const buildDefaultFormSchema = (isCommitted, generateLocked) => [
    {
        groupId: 1,
        label: "Auto Generate",
        fields: [
            { name: "AutoGenerateQuantity", tableName: "SerialNumberReceipt", label: "Auto Generate Quantity", type: "number", required: false, visible: true, readOnly: isCommitted || generateLocked },
            { name: "ItemDescription", tableName: "SerialNumberReceipt", label: "Item Description", type: "text", required: false, visible: true, readOnly: true },
            { name: "LotDescription", tableName: "SerialNumberReceipt", label: "Lot Description", type: "text", required: false, visible: true, readOnly: true },
        ],
    },
    {
        groupId: 2,
        label: "Default Values - Standard",
        fields: [
            { name: "NetWeight", tableName: "SerialNumberReceipt", label: "Net Weight", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "GrossWeight", tableName: "SerialNumberReceipt", label: "Gross Weight", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "Area1", tableName: "SerialNumberReceipt", label: "Area 1", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "Volume1", tableName: "SerialNumberReceipt", label: "Volume 1", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "Size1", tableName: "SerialNumberReceipt", label: "Size 1", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "Size2", tableName: "SerialNumberReceipt", label: "Size 2", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
        ],
    },
    {
        groupId: 3,
        label: "Default Values - Metric",
        fields: [
            { name: "NetWeight2", tableName: "SerialNumberReceipt", label: "Net Weight 2", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "GrossWeight2", tableName: "SerialNumberReceipt", label: "Gross Weight 2", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "Area2", tableName: "SerialNumberReceipt", label: "Area 2", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
            { name: "Volume2", tableName: "SerialNumberReceipt", label: "Volume 2", type: "number", required: false, visible: true, readOnly: isCommitted, format: numberFormat },
        ],
    },
    {
        groupId: 4,
        label: "Default Values",
        fields: [
            { name: "Reference1", tableName: "SerialNumberReceipt", label: "Reference 1", type: "text", required: false, visible: true, readOnly: isCommitted, maxLength: 50 },
            { name: "Reference2", tableName: "SerialNumberReceipt", label: "Reference 2", type: "text", required: false, visible: true, readOnly: isCommitted, maxLength: 50 },
            { name: "Code1",layout:CodeLookupLayout({codeType:'B4'}), tableName: "SerialNumberReceipt", label: "Code 1", type: "lookup", required: false, visible: true, readOnly: isCommitted, maxLength: 10 },
            { name: "Code2",layout:CodeLookupLayout({codeType:'B5'}), tableName: "SerialNumberReceipt", label: "Code 2", type: "lookup", required: false, visible: true, readOnly: isCommitted, maxLength: 10 },
            { name: "ReceiptVerified", tableName: "SerialNumberReceipt", label: "Receipt Verified", type: "select", required: false, visible: true, readOnly: isCommitted },
        ],
    },
];

/**
 * OM Serial Number Receipt entry popup — web port of the legacy
 * Argos.Insight.Win.OM.Views.SerialNumberReceiptController screen (Credit orders only).
 */
function SerialNumberReceiptPopup(props) {
    const { showPopup = {}, setShowPopup, oidc } = props;
    const visible = showPopup.visible === true;
    const detailRowId = showPopup.detailRowId;
    const currentUser = oidc?.user;
    const accessToken = currentUser?.access_token;
    const DYNAMICFORMSETTING = currentUser?.profile?.DYNAMICFORMSETTING;

    const dataGridRef = useRef(null);
    const formRefs = useRef({});
    const [receiptData, setReceiptData] = useState(null);
    const [formFields, setformFields] = useState(initialFormFields);
    const [generateDisabled, setGenerateDisabled] = useState(true);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const isCommitted = receiptData?.IsCommitted === true;

    /* FormField memoizes handleFieldValueChange from the first render, so the conversion
       factors are read through a ref to stay current. */
    const factorsRef = useRef({});
    useEffect(() => {
        factorsRef.current = {
            weight: receiptData?.WeightConversionFactor ?? null,
            volume: receiptData?.VolumeConversionFactor ?? null,
            area: receiptData?.AreaConversionFactor ?? null,
        };
    }, [receiptData]);

    const closePopup = useCallback(() => {
        setReceiptData(null);
        setformFields(initialFormFields);
        setGenerateDisabled(true);
        setSelectedRowKeys([]);
        formRefs.current = {};
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    useEffect(() => {
        if (!visible || !detailRowId || !accessToken) return;
        let cancelled = false;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetSerialNumberReceiptData}`, {
                params: { detailRowId },
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                if (cancelled) return;
                const payload = res.data || {};
                if (payload.Success === false || !payload.Resultdata) {
                    alert(payload.Message || MESSAGES.ERROR, MESSAGES.ERROR).then(closePopup);
                    return;
                }
                const data = payload.Resultdata;
                setReceiptData(data);
                setformFields((prev) => ({
                    ...prev,
                    ItemDescription: data.ItemDescription,
                    LotDescription: data.LotDescription,
                }));
                setGenerateDisabled(data.HasSerialNumbers === true || data.IsCommitted === true);
            })
            .catch((err) => {
                if (cancelled) return;
                const msg = err.response?.data?.message || err.message;
                alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR).then(closePopup);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, detailRowId, accessToken, closePopup]);

    const dataSource = useMemo(() => {
        if (!visible || !detailRowId || !accessToken) return null;
        return createStore({
            key: "RowId",
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetReceiptSerialNumbers}`,
            insertUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.AddReceiptSerialNumber}`,
            updateUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.EditReceiptSerialNumber}`,
            deleteUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.DeleteReceiptSerialNumber}`,
            loadParams: { detailRowId },
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
    }, [visible, detailRowId, accessToken]);

    /* Locked once serial numbers exist or after Generate has run (legacy behavior). */
    const hasSerialNumbers = receiptData?.HasSerialNumbers === true;
    const defaultFormSchema = useMemo(
        () => buildDefaultFormSchema(isCommitted, hasSerialNumbers),
        [isCommitted, hasSerialNumbers]
    );

    /* Recurring-review rule: coded fields render as descriptive dropdowns; select options
       are loaded through formRefs in onFormLoad (weekSettings PrimaryTab pattern). */
    const receiptVerifiedValue = formFields?.ReceiptVerified;
    const loadReceiptVerifiedOptions = useCallback(() => {
        const ref = formRefs.current["ReceiptVerified"];
        /* instance() returns undefined while the widget is not yet created or already
           disposed (stale ref from a previous popup open) — never call option() on it. */
        const instance = ref?.instance ? ref.instance() : null;
        if (!instance) return;
        instance.option("dataSource", yesNoItems);
        instance.option("valueExpr", "ID");
        instance.option("displayExpr", "Text");
        if (receiptVerifiedValue !== undefined) {
            instance.option("value", receiptVerifiedValue);
        }
    }, [receiptVerifiedValue]);

    const handleFormLoad = useCallback(() => {
        setTimeout(() => {
            loadReceiptVerifiedOptions();
        }, 100);
    }, [loadReceiptVerifiedOptions]);

    useEffect(() => {
        loadReceiptVerifiedOptions();
    }, [loadReceiptVerifiedOptions]);

    /* Standard <-> metric default-value sync. Only user edits (e.event set) recompute the
       pair, so the programmatic update of the other field cannot ping-pong back. */
    const handleFieldValueChange = useCallback((name, value, e) => {
        if (!e?.event) return;
        const pair = conversionPairs[name];
        if (!pair) return;
        const factor = factorsRef.current[pair.factor];
        if (factor == null || (!pair.multiply && factor === 0)) return;
        let converted = value == null ? 0 : pair.multiply ? value * factor : value / factor;
        if (pair.rounded) converted = roundAwayFromZero(converted);
        setformFields((prev) => ({ ...prev, [pair.target]: converted }));
    }, []);

    const onInitNewRow = useCallback(
        (e) => {
            e.data = {
                SerialNumber: null,
                NetWeight: formFields.NetWeight,
                NetWeight2: formFields.NetWeight2,
                GrossWeight: formFields.GrossWeight,
                GrossWeight2: formFields.GrossWeight2,
                Area1: formFields.Area1,
                Area2: formFields.Area2,
                Volume1: formFields.Volume1,
                Volume2: formFields.Volume2,
                Size1: formFields.Size1,
                Size2: formFields.Size2,
                Code1: formFields.Code1,
                Code2: formFields.Code2,
                Reference1: formFields.Reference1,
                Reference2: formFields.Reference2,
                ReceiptVerified: formFields.ReceiptVerified || "N",
                DamageFlag: "N",
                DamageDescription: null,
                RInDetail: detailRowId,
            };
        },
        [formFields, detailRowId]
    );
    /* Generate inserts ONE default row into grid memory; the user types the Serial #
       and the row is saved when they leave it (cell editing auto-commits the row). */
    const handleGenerate = useCallback(() => {
        const grid = dataGridRef.current?.instance();
        if (!grid) return;
        grid.addRow();
    }, []);

    /* Legacy grid cell conversions: editing a standard weight recalculates the metric one. */
    const weightFactor = receiptData?.WeightConversionFactor ?? null;
    const setNetWeightCellValue = useCallback(
        (newData, value) => {
            newData.NetWeight = value;
            if (weightFactor != null) newData.NetWeight2 = roundAwayFromZero(value == null ? 0 : value * weightFactor);
        },
        [weightFactor]
    );
    const setGrossWeightCellValue = useCallback(
        (newData, value) => {
            newData.GrossWeight = value;
            if (weightFactor != null) newData.GrossWeight2 = roundAwayFromZero(value == null ? 0 : value * weightFactor);
        },
        [weightFactor]
    );

    const handleHiding = useCallback(
        (e) => {
            const grid = dataGridRef.current?.instance();
            if (grid && grid.hasEditData()) {
                e.cancel = true;
                confirm(
                    "<i>Serial Numbers exist on the grid which have not been applied to the database.  Do you wish to continue?</i>",
                    "Data Not Saved"
                ).then((dialogResult) => {
                    if (dialogResult) {
                        grid.cancelEditData();
                        closePopup();
                    }
                });
                return;
            }
            closePopup();
        },
        [closePopup]
    );

    const onSaved = useCallback(() => {
        notify({ message: "Serial Number saved.", width: 250 }, "success", 3000);
    }, []);

    const handleSelectionChanged = useCallback((e) => {
        setSelectedRowKeys(e.selectedRowKeys);
    }, []);

    /* DetailsTab-style delete: trash button in the grid toolbar removes the selected row
       through the grid's store (DeleteSerialNumber endpoint) after confirmation. */
    const deleteSelectedRecord = useCallback(() => {
        const grid = dataGridRef.current?.instance();
        const key = selectedRowKeys[0];
        if (!grid || !key) return;
        confirm("<i>Are your sure you want to delete selected records?</i>", "Delete").then((dialogResult) => {
            if (!dialogResult) return;
            grid.beginCustomLoading("Deleting");
            grid.getDataSource()
                .store()
                .remove(key)
                .then(() => {
                    grid.endCustomLoading();
                    setSelectedRowKeys([]);
                    grid.refresh();
                    notify({ message: "Serial Number deleted.", width: 250 }, "success", 3000);
                })
                .catch((err) => {
                    grid.endCustomLoading();
                    const msg = err?.message || err;
                    alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
                });
        });
    }, [selectedRowKeys]);

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={showPopup.title ?? "Serial Number Receipt"}
            container="#root"
            width="85vw"
            height="90vh"
        >
            <div style={{ height: "100%", overflowY: "auto", padding: "4px 8px" }}>
                {receiptData && (
                    <>
                        <div className="row" style={{ marginBottom: 4, justifyContent: "flex-end", marginRight: 0 }}>
                            <Button
                                text="Generate"
                                type="default"
                                stylingMode="contained"
                                disabled={generateDisabled}
                                onClick={handleGenerate}
                            />
                        </div>
                        <FormField
                            currrentUser={currentUser}
                            layoutId={SERIAL_NUMBER_RECEIPT_LAYOUT_ID}
                            layoutName="OMSerialNumberReceiptForm"
                            defaultFormSchema={defaultFormSchema}
                            formFields={formFields}
                            setformFields={setformFields}
                            handleFieldValueChange={handleFieldValueChange}
                            DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                            formRefs={formRefs}
                            onFormLoad={handleFormLoad}
                            noSpacingBeforeFirstRow={true}
                        />
                    </>
                )}
                <div style={{ marginTop: 8 }}>
                    <DataGrid
                        ref={dataGridRef}
                        dataSource={dataSource}
                        selectedRowKeys={selectedRowKeys}
                        onSelectionChanged={handleSelectionChanged}
                        showBorders={true}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        allowColumnReordering={true}
                        rowAlternationEnabled={true}
                        height="45vh"
                        onInitNewRow={onInitNewRow}
                        onSaved={onSaved}
                        onContentReady={onContentReady}
                        onEditingStart={(e)=>onEditingStart(e,dataGridRef)}
                    >
                        <Selection mode="single" />
                        <Scrolling mode="standard" />
                        <GridLoadPanel enabled={true} />
                        <Editing
                            mode="cell"
                            allowAdding={!isCommitted}
                            allowUpdating={!isCommitted}
                            useIcons={true}
                        />
                        <Column dataField="RowId" visible={false} allowEditing={false} />
                        <Column dataField="RInDetail" visible={false} allowEditing={false} />
                        <Column dataField="SerialNumber" caption="Serial #">
                            <RequiredRule message="The Serial # field is required." />
                        </Column>
                        <Column dataField="NetWeight" caption="Net Weight" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} setCellValue={setNetWeightCellValue} />
                        <Column dataField="GrossWeight" caption="Gross Weight" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} setCellValue={setGrossWeightCellValue} />
                        <Column dataField="Code1" caption="Code 1" editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                        loadParams: {
                            lookupType: function () {
                                return "ImCode";
                            },
                            module: function () {
                                return "IM";
                            },
                           codetype: function () {
                                return "B4";
                            },
                            isPascalCase: function () {
                                return true;
                            }
                        },
                        loadUrl: `${window._env_.REACT_APP_APIS}/${routes.Common.GetLookup}`,
                        key: "Id",
                        displayExpr: "Name",
                        valueExpr: "Id",
                        accessToken: accessToken,
                        defaultValue: cellElement.data.Code1,
                        sort: [{ selector: "Name", desc: false }],
                        onValueChangeCallback: (value, text) => {
                            cellElement.setValue(({ value, text }));
                        },
                    })
                }
                    setCellValue={(rowData, newValue) => {
                        rowData.Code1 = newValue.text;
                    }}  />
                        <Column dataField="Size1" caption="Size 1" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                        <Column dataField="Size2" caption="Size 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                        <Column dataField="Reference1" caption="Reference 1" />
                        <Column dataField="DamageFlag" caption="Damage Flag">
                            <Lookup dataSource={yesNoItems} valueExpr="ID" displayExpr="Text" />
                        </Column>
                        <Column dataField="DamageDescription" caption="Damage Description" />
                        <Column dataField="ReceiptVerified" caption="Receipt Verified" visible={false}>
                            <Lookup dataSource={yesNoItems} valueExpr="ID" displayExpr="Text" />
                        </Column>
                        <Column dataField="NetWeight2" caption="Net Weight 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="GrossWeight2" caption="Gross Weight 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Area1" caption="Area 1" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Area2" caption="Area 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Volume1" caption="Volume 1" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Volume2" caption="Volume 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} visible={false} />
                        <Column dataField="Code2" caption="Code 2" visible={false} />
                        <Column dataField="Reference2" caption="Reference 2" visible={false} />
                        <GridToolbar>
                            <GridToolbarItem name="addRowButton" />
                            <GridToolbarItem location="after">
                                <Button
                                    icon="trash"
                                    onClick={deleteSelectedRecord}
                                    disabled={isCommitted || !selectedRowKeys.length}
                                />
                            </GridToolbarItem>
                        </GridToolbar>
                    </DataGrid>
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SerialNumberReceiptPopup);
